import {useEffect,useRef,useState} from 'react';
import type {Snapshot} from '@platform/contracts/x';
import {hasParticipation,isFinished,nextDraw} from './roomFlow';

export {isFinished} from './roomFlow';

// Follow an owned successor only after the server releases the final result.
// Keep only one viewed snapshot, not an ever-growing client history.
export function useViewedDraw(current:Snapshot|null,previous:Snapshot|null){
 const [saved,setSaved]=useState<Snapshot|null>(null);
 const [followedFrom,setFollowedFrom]=useState<Snapshot|null>(null);
 const recoveredId=useRef('');
 const observed=!saved?current:current?.drawId===saved.drawId?current:current?.upcoming?.drawId===saved.drawId?current.upcoming:previous?.drawId===saved.drawId&&previous.revision>saved.revision?previous:saved;
 // Only a confirmed result permits automatic spectator following. A missed
 // result is recovered first: it may contain an accepted purchase/refund.
 // With a configured Tombala hold, the server promotes the successor at expiry.
 // A client popup dismissal must not shorten that shared result interval.
 const serverHolding=observed?.drawId===current?.drawId&&observed?.status==='Completed'&&observed.awardPause?.stage==='full-house';
 const successor=observed&&current?nextDraw(observed,current):null;
 const following=observed&&isFinished(observed)&&!serverHolding&&successor&&(!hasParticipation(observed)||hasParticipation(successor))?successor:null;
 const view=following??observed;
 // Keep the confirmed result for passive card scenery even when recovery must
 // suppress old notifications. Both use the same single retained result.
 const previousResult=following?observed:followedFrom;
 const transition=previousResult?.drawId===recoveredId.current?null:previousResult;
 const choose=(draw:Snapshot)=>{recoveredId.current='';setFollowedFrom(null);setSaved(draw);};
 useEffect(()=>{if(view)setSaved(view);if(following)setFollowedFrom(observed);},[view,following,observed]);
 const refundPending=(draw:Snapshot)=>draw.tombala.status==='Cancelled'&&draw.tombala.refundedMinor<draw.tombala.ownTotalMinor;
 const missingId=view&&current&&view.drawId!==current.drawId&&view.drawId!==current.upcoming?.drawId&&(!isFinished(view)||refundPending(view))?view.drawId:'';
 useEffect(()=>{
  if(!missingId)return;
  const abort=new AbortController();let retry:ReturnType<typeof setTimeout>;
  const recover=async()=>{
   try{
    const response=await fetch('/api/tombala/draws/'+encodeURIComponent(missingId),{signal:abort.signal});
    if(!response.ok)throw new Error('Draw result unavailable');
    const result=await response.json() as Snapshot;
    if(!isFinished(result)||result.drawId!==missingId)throw new Error('Draw result not final');
    if(!abort.signal.aborted){recoveredId.current=missingId;setSaved(previous=>previous?.drawId===missingId?result:previous);if(refundPending(result))retry=setTimeout(()=>void recover(),3000);}
   }catch{if(!abort.signal.aborted)retry=setTimeout(()=>void recover(),3000);}
  };
  void recover();return()=>{abort.abort();clearTimeout(retry);};
 },[missingId]);
 return {view,choose,transition,previousResult,resultPending:!!missingId&&!!view&&!isFinished(view)};
}
